import Pusher from 'pusher-js';
import * as PusherPushNotifications from '@pusher/push-notifications-web';
import { createContext, useContext, useEffect, useMemo } from 'react';
// redux
import { useSelector } from '../redux/store';
import { selectCurrentAuth } from '../redux/slices/auth';
// ----------------------------------------------------------------------

const initialState = new Pusher(`${import.meta.env.VITE_APP_KEY}`, {
  cluster: `${import.meta.env.VITE_APP_CLUSTER}`,
});

export const NotificationContext = createContext(initialState);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);

  if (!context) throw new Error('useNotificationContext must be use inside SettingsProvider');

  return context;
};

// ----------------------------------------------------------------------

export const NotificationProvider = ({ children }: { children: any }) => {
  const { user, token } = useSelector(selectCurrentAuth);

  const pusher = useMemo(
    () =>
      new Pusher(`${import.meta.env.VITE_APP_KEY}`, {
        cluster: `${import.meta.env.VITE_APP_CLUSTER}`,
        authEndpoint: `${import.meta.env.VITE_APP_API_URL}/user/notifications/auth`,
        auth: {
          params: {
            userId: user?.id,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      }),
    [user?.id, token]
  );

  useEffect(() => {
    let beamsClient: PusherPushNotifications.Client | null = null;

    const initBeams = async () => {
      try {
        if (import.meta.env.VITE_APP_PUSH_INSTANCE_ID && user?.id && token) {
          const serviceWorkerRegistration = await window.navigator.serviceWorker.ready;

          beamsClient = new PusherPushNotifications.Client({
            instanceId: `${import.meta.env.VITE_APP_PUSH_INSTANCE_ID}`,
            serviceWorkerRegistration,
          });

          const beamsTokenProvider = new PusherPushNotifications.TokenProvider({
            url: `${import.meta.env.VITE_APP_API_URL}/user/notifications/pusher/beams-auth`,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          await beamsClient.start();
          await beamsClient.setUserId(user?.id, beamsTokenProvider);
        }
      } catch (error) {
        console.error('Pusher Beams error:', error);
      }
    };

    initBeams();

    return () => {
      if (beamsClient) {
        beamsClient.stop().catch(console.error);
      }
    };
  }, [user?.id, token]);

  return <NotificationContext.Provider value={pusher}>{children}</NotificationContext.Provider>;
};
